import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../Sidebar";
import { Button } from "../ui/Button";
import FinancialReviewTab from "./FinancialReviewTab";
import AnalysisTab from "./AnalysisTab";
import RetentionTab from "./RetentionTab";
import LtvTab from "./LtvTab";
import {
  fetchFinancialReviewFilters,
  fetchFinancialReviewReport,
  fetchAnalysis,
  fetchRetention,
  fetchLtv,
  syncPylonTickets,
} from "./api";
import { ALL_VALUE, encodeFilterList, encodeVisnList, filterList, optionListScope, visnList } from "./optionListScope";
import { MultiSelect } from "./ui";

const EMPTY_FILTER_OPTIONS = { visns: [], specialties: [], stations: [], states: [], riseVisns: [] };

const filterScopeKey = ({ visn, stationId, specialty, licenseState, riseVisn, reportMonth }) =>
  JSON.stringify({ visn, stationId, specialty, licenseState, riseVisn, reportMonth });

const toApiFilters = ({ visn, stationId, specialty, licenseState, riseVisn, reportMonth }) => ({
  visn: visn === ALL_VALUE ? undefined : visn,
  station_id: stationId === ALL_VALUE ? undefined : stationId,
  specialty: specialty === ALL_VALUE ? undefined : specialty,
  report_month: reportMonth || undefined,
  state: licenseState === ALL_VALUE ? undefined : licenseState,
  new_visn: riseVisn === ALL_VALUE ? undefined : riseVisn,
});

// Lists are only shown when they were fetched for the current filters.
// Otherwise keep the current selection visible so a previous scope can't be
// picked and the Select doesn't go blank.
const pinnedFilterOptions = ({ visn, stationId, specialty, licenseState, riseVisn, stations = [] }) => {
  const selectedVisns = visnList(visn);
  const selectedStationIds = filterList(stationId);
  return {
    visns: selectedVisns.map((v) => parseInt(v, 10)),
    specialties: filterList(specialty),
    stations: selectedStationIds.map(
      (id) =>
        stations.find((s) => s.station_id === id) || {
          station_id: id,
          site_name: "",
          visn: selectedVisns.length === 1 ? parseInt(selectedVisns[0], 10) : null,
        }
    ),
    states: filterList(licenseState),
    riseVisns: visnList(riseVisn).map((v) => parseInt(v, 10)),
  };
};
const MONTH_INPUT_CLASS = "flex h-10 w-[160px] rounded-md border border-input bg-background px-3 py-2 text-sm";
const ANALYSIS_DEFAULT_MONTHS = 6;

const utcMonthInput = (date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

const defaultAnalysisEndMonth = () => {
  const now = new Date();
  return utcMonthInput(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));
};

const shiftMonthInput = (yyyyMm, delta) => {
  const [year, month] = yyyyMm.split("-").map(Number);
  return utcMonthInput(new Date(Date.UTC(year, month - 1 + delta, 1)));
};

const DEFAULT_ANALYSIS_END_MONTH = defaultAnalysisEndMonth();
const DEFAULT_ANALYSIS_START_MONTH = shiftMonthInput(DEFAULT_ANALYSIS_END_MONTH, -(ANALYSIS_DEFAULT_MONTHS - 1));
const TABS = ["financialreview", "analysis", "retention", "ltv"];
const DEFAULT_TAB = "financialreview";
const emptyFilters = () => ({
  visn: ALL_VALUE,
  stationId: ALL_VALUE,
  specialty: ALL_VALUE,
  licenseState: ALL_VALUE,
  riseVisn: ALL_VALUE,
  reportMonth: "",
  analysisStartMonth: DEFAULT_ANALYSIS_START_MONTH,
  analysisEndMonth: DEFAULT_ANALYSIS_END_MONTH,
});

export default function FinancialReview({ setAuth, setUserOrg }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const activeTab = TABS.includes(tabFromUrl) ? tabFromUrl : DEFAULT_TAB;
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === DEFAULT_TAB) next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next);
  };

  const [fetchedFilterOptions, setFetchedFilterOptions] = useState({
    scopeKey: null,
    ...EMPTY_FILTER_OPTIONS,
  });
  // Guards against out-of-order responses: loadFilterOptions can fire again
  // before an earlier call's request resolves (e.g. quickly toggling two
  // filters), and network timing gives no guarantee the responses arrive in
  // the order they were sent. Without this, a slower, now-stale response
  // could land last and overwrite the options for whatever is currently
  // selected.
  const filterOptionsRequestId = useRef(0);
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);

  const [financialReviewData, setFinancialReviewData] = useState(null);
  const [financialReviewLoading, setFinancialReviewLoading] = useState(true);
  const [financialReviewError, setFinancialReviewError] = useState(null);

  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  // Retention/LTV (beta) and Analysis (multi-month series) are heavier than
  // the Financial Review tab, so they are fetched lazily — only once their
  // tab is actually selected, and again whenever the filters change while
  // that tab stays selected.
  const [retentionData, setRetentionData] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionError, setRetentionError] = useState(null);

  const [ltvData, setLtvData] = useState(null);
  const [ltvLoading, setLtvLoading] = useState(false);
  const [ltvError, setLtvError] = useState(null);

  // Live Pylon sync (beta) — a manual "pull now" action rather than
  // something that runs automatically, since it calls an external API and
  // can take a few seconds. Its own status is separate from
  // retentionLoading/Error since it's a distinct action, not the tab's data
  // fetch.
  const [ticketSyncStatus, setTicketSyncStatus] = useState(null); // null | "syncing" | "done" | "error"
  const [ticketSyncMessage, setTicketSyncMessage] = useState(null);

  const activeFilters = toApiFilters(applied);

  const optionListFilters = optionListScope(activeTab, applied);
  const currentFilterScopeKey = filterScopeKey(optionListFilters);
  const filterOptions =
    fetchedFilterOptions.scopeKey === currentFilterScopeKey
      ? fetchedFilterOptions
      : pinnedFilterOptions({
          ...applied,
          stations: fetchedFilterOptions.stations,
        });

  // Refetched whenever a visible filter or the report month changes.
  // Each dropdown is scoped to the other filters the current tab applies
  // -- e.g. picking a RISE VISN on Financial Review narrows VISN/Specialty/
  // State/Site to matching data that month. Hidden tab filters are omitted
  // so Analysis/Retention/LTV lists match what those reports actually use.
  const loadFilterOptions = useCallback(() => {
    const requestId = ++filterOptionsRequestId.current;
    const scopeKey = currentFilterScopeKey;
    fetchFinancialReviewFilters(toApiFilters(optionListFilters))
      .then((res) => {
        if (requestId !== filterOptionsRequestId.current) return;
        setFetchedFilterOptions({
          scopeKey,
          visns: res.visns,
          specialties: res.specialties,
          stations: res.stations,
          states: res.states,
          riseVisns: res.rise_visns || [],
        });
      })
      .catch(() => {
        // Leave fetchedFilterOptions on its previous scopeKey so the render
        // path above keeps showing pinned current-selection lists.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, applied]);

  const loadFinancialReview = useCallback(() => {
    setFinancialReviewLoading(true);
    setFinancialReviewError(null);
    fetchFinancialReviewReport(activeFilters)
      .then((res) => setFinancialReviewData(res))
      .catch((err) => setFinancialReviewError(err.message))
      .finally(() => setFinancialReviewLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const loadAnalysis = useCallback(() => {
    setAnalysisLoading(true);
    setAnalysisError(null);
    fetchAnalysis({
      visn: applied.visn === ALL_VALUE ? undefined : applied.visn,
      station_id: applied.stationId === ALL_VALUE ? undefined : applied.stationId,
      specialty: applied.specialty === ALL_VALUE ? undefined : applied.specialty,
      start_month: applied.analysisStartMonth || undefined,
      end_month: applied.analysisEndMonth || undefined,
    })
      .then((res) => setAnalysisData(res))
      .catch((err) => setAnalysisError(err.message))
      .finally(() => setAnalysisLoading(false));
  }, [
    applied.visn,
    applied.stationId,
    applied.specialty,
    applied.analysisStartMonth,
    applied.analysisEndMonth,
  ]);

  const loadRetention = useCallback(() => {
    setRetentionLoading(true);
    setRetentionError(null);
    fetchRetention(activeFilters)
      .then((res) => setRetentionData(res))
      .catch((err) => setRetentionError(err.message))
      .finally(() => setRetentionLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied.visn, applied.stationId, applied.specialty, applied.reportMonth]);

  // LTV is all-time by design (see LtvTab's description) — reportMonth isn't
  // in this dependency list since the backend route doesn't read it, so
  // changing the Report month filter alone won't needlessly refetch this tab.
  const loadLtv = useCallback(() => {
    setLtvLoading(true);
    setLtvError(null);
    fetchLtv(activeFilters)
      .then((res) => setLtvData(res))
      .catch((err) => setLtvError(err.message))
      .finally(() => setLtvLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied.visn, applied.stationId, applied.specialty]);

  // Pulls live from Pylon, then reloads the Retention tab's data so the
  // newly-synced tickets show up immediately without a manual refresh.
  const handleSyncPylonTickets = useCallback(() => {
    setTicketSyncStatus("syncing");
    setTicketSyncMessage(null);
    syncPylonTickets()
      .then((res) => {
        setTicketSyncStatus("done");
        setTicketSyncMessage(`Synced ${res.imported} ticket${res.imported === 1 ? "" : "s"}.`);
        loadRetention();
      })
      .catch((err) => {
        setTicketSyncStatus("error");
        setTicketSyncMessage(err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRetention]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadFinancialReview();
  }, [loadFinancialReview]);

  useEffect(() => {
    if (activeTab === "analysis") loadAnalysis();
  }, [activeTab, loadAnalysis]);

  useEffect(() => {
    if (activeTab === "retention") loadRetention();
  }, [activeTab, loadRetention]);

  useEffect(() => {
    if (activeTab === "ltv") loadLtv();
  }, [activeTab, loadLtv]);

  const selectedVisns = visnList(draft.visn);
  const selectedStationIds = filterList(draft.stationId);
  const filteredStations =
    selectedVisns.length === 0
      ? filterOptions.stations
      : filterOptions.stations.filter((s) => selectedVisns.includes(String(s.visn)));
  const stationById = new Map(filterOptions.stations.map((s) => [s.station_id, s]));
  const filtersDirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const filtersActive =
    draft.visn !== ALL_VALUE ||
    draft.stationId !== ALL_VALUE ||
    draft.specialty !== ALL_VALUE ||
    (activeTab === "financialreview" &&
      (draft.licenseState !== ALL_VALUE || draft.riseVisn !== ALL_VALUE || draft.reportMonth)) ||
    (activeTab === "retention" && draft.reportMonth) ||
    (activeTab === "analysis" &&
      (draft.analysisStartMonth !== DEFAULT_ANALYSIS_START_MONTH ||
        draft.analysisEndMonth !== DEFAULT_ANALYSIS_END_MONTH));

  return (
    <div className="flex">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      {/* min-w-0 overrides the flex item's default min-width: auto — without
          it, this column refuses to shrink below the intrinsic width of its
          widest child (the Loyalty heatmap / Returning users tables) and
          the whole page grows instead of letting each table's own
          overflow-x-auto wrapper scroll within the available width. */}
      <div className="flex-1 min-w-0 p-6 space-y-6" style={{ marginLeft: "var(--sidebar-width, 250px)" }}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Financial Review</h1>
        </div>
        <div className="flex gap-2">
          <Button variant={activeTab === "financialreview" ? "default" : "outline"} onClick={() => setActiveTab("financialreview")}>
            Financial Review
          </Button>
          <Button
            variant={activeTab === "analysis" ? "default" : "outline"}
            onClick={() => setActiveTab("analysis")}
          >
            Analysis
          </Button>
          <Button
            variant={activeTab === "retention" ? "default" : "outline"}
            onClick={() => setActiveTab("retention")}
          >
            Retention (beta)
          </Button>
          <Button
            variant={activeTab === "ltv" ? "default" : "outline"}
            onClick={() => setActiveTab("ltv")}
          >
            LTV (beta)
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">VISN</label>
          <MultiSelect
            className="w-[160px]"
            values={selectedVisns}
            options={filterOptions.visns}
            allLabel="All VISNs"
            formatValue={(value) => `VISN ${value}`}
            summaryLabel={(count) => `${count} VISNs`}
            onChange={(values) => {
              const next = encodeVisnList(values);
              setDraft((prev) => ({
                ...prev,
                visn: next,
                stationId: JSON.stringify(next) !== JSON.stringify(prev.visn) ? ALL_VALUE : prev.stationId,
              }));
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Site</label>
          <MultiSelect
            className="w-[220px]"
            values={selectedStationIds}
            options={[...new Set([...filteredStations.map((s) => s.station_id), ...selectedStationIds])]}
            allLabel="All sites"
            formatValue={(id) => {
              const site = stationById.get(id);
              return site ? `${site.station_id} — ${site.site_name}` : id;
            }}
            onChange={(values) => setDraft((prev) => ({ ...prev, stationId: encodeFilterList(values) }))}
            summaryLabel={(count) => `${count} sites`}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Specialty</label>
          <MultiSelect
            className="w-[200px]"
            values={filterList(draft.specialty)}
            options={filterOptions.specialties}
            allLabel="All specialties"
            onChange={(values) => setDraft((prev) => ({ ...prev, specialty: encodeFilterList(values) }))}
            summaryLabel={(count) => `${count} specialties`}
          />
        </div>

        {activeTab === "financialreview" && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">State</label>
              <MultiSelect
                className="w-[140px]"
                values={filterList(draft.licenseState)}
                options={filterOptions.states}
                allLabel="All states"
                onChange={(values) => setDraft((prev) => ({ ...prev, licenseState: encodeFilterList(values) }))}
                summaryLabel={(count) => `${count} states`}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">RISE VISN</label>
              <MultiSelect
                className="w-[160px]"
                values={visnList(draft.riseVisn)}
                options={filterOptions.riseVisns}
                allLabel="All RISE VISNs"
                formatValue={(value) => `VISN ${value}`}
                onChange={(values) => setDraft((prev) => ({ ...prev, riseVisn: encodeVisnList(values) }))}
                summaryLabel={(count) => `${count} RISE VISNs`}
              />
            </div>
          </>
        )}

        {activeTab === "analysis" && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Start month</label>
              <input
                type="month"
                className={MONTH_INPUT_CLASS}
                value={draft.analysisStartMonth}
                max={draft.analysisEndMonth || undefined}
                onChange={(e) => setDraft((prev) => ({ ...prev, analysisStartMonth: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">End month</label>
              <input
                type="month"
                className={MONTH_INPUT_CLASS}
                value={draft.analysisEndMonth}
                min={draft.analysisStartMonth || undefined}
                onChange={(e) => setDraft((prev) => ({ ...prev, analysisEndMonth: e.target.value }))}
              />
            </div>
          </>
        )}

        {(activeTab === "financialreview" || activeTab === "retention") && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Report month</label>
            <input
              type="month"
              className={MONTH_INPUT_CLASS}
              value={draft.reportMonth}
              onChange={(e) => setDraft((prev) => ({ ...prev, reportMonth: e.target.value }))}
            />
          </div>
        )}

        <Button disabled={!filtersDirty} onClick={() => setApplied({ ...draft })}>
          Submit
        </Button>
        {filtersActive && (
          <Button
            variant="ghost"
            onClick={() => {
              const cleared = emptyFilters();
              setDraft(cleared);
              setApplied(cleared);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {activeTab === "financialreview" && (
        <FinancialReviewTab data={financialReviewData} loading={financialReviewLoading} error={financialReviewError} />
      )}
      {activeTab === "analysis" && (
        <AnalysisTab data={analysisData} loading={analysisLoading} error={analysisError} />
      )}
      {activeTab === "retention" && (
        <RetentionTab
          data={retentionData}
          loading={retentionLoading}
          error={retentionError}
          onSyncTickets={handleSyncPylonTickets}
          ticketSyncStatus={ticketSyncStatus}
          ticketSyncMessage={ticketSyncMessage}
        />
      )}
      {activeTab === "ltv" && (
        <LtvTab data={ltvData} loading={ltvLoading} error={ltvError} />
      )}
      </div>
    </div>
  );
}
